import React, { createContext, useState, useContext, useMemo, useCallback } from 'react';
import get from 'lodash.get';

import useDataApiEffect from '../../../hooks/useDataApiEffect';
import productsService from '../../services/products.service';
import { PreviousOrder } from '../../../types/api/patient/PreviousOrder';
import useStartConsultation from '../../../components/Consultation/hooks/useStartConsultation';

import ConsultationReorderConfirm from '../../../components/Consultation/ConsultationReorderConfirm/ConsultationReorderConfirm';
import ModalErrorBoundary from '../../../components/ErrorBoundaries/ModalErrorBoundary';
import Modal from '../../../components/_ui/Modal/Modal';

interface ContextReturnValues {
    setTreatmentToReorder: (treatment: PreviousOrder | null) => void;
}

// Start of the consultation treatment context.
const ConsultationReorderContext = createContext<unknown>({});

/**
 * The provider for the consultation treatment.
 * @returns React elements
 */
export const ConsultationReorderProvider = ({ children }: { children: React.ReactNode }) => {
    const [treatmentToReorder, setReorder] = useState<PreviousOrder | null>(null);
    const startConsultation = useStartConsultation();

    const treatmentId = treatmentToReorder?.parent?.reference || treatmentToReorder?.reference || null;

    const [{ isLoading: loadingTreatment, data, error: treatmentError } /* */, , reset] = useDataApiEffect(
        treatmentId ? () => productsService.getProduct(treatmentId) : null,
        [treatmentToReorder]
    );

    const handleClose = () => {
        reset({ isLoading: true }); // Need to set the loading state back to true on close.
        setReorder(null);
    };

    const treatment = useMemo(() => get(data, 'data') || null, [data]);

    const setTreatmentToReorder = useCallback(async (reorder: PreviousOrder) => {
        if (!reorder?.configuration?.titrations?.enabled) {
            setReorder(reorder);
            return;
        }

        const conditionSlug = !Array.isArray(reorder.public) ? reorder.public.condition.slug : '';

        if (!conditionSlug) {
            return;
        }

        await startConsultation(conditionSlug, {
            conditionId: reorder.condition_id,
            treatmentId: reorder.reference,
            conditionName: reorder.condition,
            quantity: reorder.quantity,
        });
    }, []);

    const value = useMemo(
        () => ({
            setTreatmentToReorder,
        }),
        [setTreatmentToReorder]
    );

    return (
        <ConsultationReorderContext.Provider value={value}>
            {treatmentToReorder ? (
                <Modal show={!!treatmentToReorder} onClose={handleClose}>
                    <ModalErrorBoundary onClose={handleClose}>
                        <ConsultationReorderConfirm
                            loadingTreatment={loadingTreatment}
                            treatmentError={treatmentError}
                            treatment={treatment}
                            treatmentToReorder={treatmentToReorder}
                            onClose={handleClose}
                        />
                    </ModalErrorBoundary>
                </Modal>
            ) : null}
            {children}
        </ConsultationReorderContext.Provider>
    );
};

/**
 * Hook to be used in components that will allow us access to the values passed into the search provider
 * above.
 * @param {*} showContextError - sometimes we want to use this but fail silently, such as we need the context on a sign up form, as its required whilst being in the consultation signup
 */
export const useConsultationReorderContext = (showContextError = true) => {
    const context = useContext<ContextReturnValues>(ConsultationReorderContext as React.Context<ContextReturnValues>);

    if (context === undefined && showContextError) {
        throw new Error('useConsultationReorderContext must be used within a ConsultationReorderProvider');
    }
    return context;
};
